import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, Trophy, Calendar, Loader2, Plus, Play, 
  ChevronRight, RefreshCw, CheckCircle, Wifi, WifiOff
} from 'lucide-react';
import { useWebSocketContext } from '../hooks/useWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pool Standing Row Component
const StandingRow = ({ standing, rank, isAdvancing }) => (
  <tr className={`border-b ${isAdvancing ? 'bg-green-50 dark:bg-green-900/20' : ''}`} data-testid={`standing-${standing.team_id}`}>
    <td className="py-3 px-4 font-medium">
      {rank}
      {isAdvancing && <Badge className="ml-2 bg-green-500">Advances</Badge>}
    </td>
    <td className="py-3 px-4 font-semibold">{standing.team_name}</td>
    <td className="py-3 px-4 text-center">{standing.wins}</td>
    <td className="py-3 px-4 text-center">{standing.losses}</td>
    <td className="py-3 px-4 text-center">{standing.ties}</td>
    <td className="py-3 px-4 text-center font-semibold">{standing.points_scored}</td>
    <td className="py-3 px-4 text-center">{standing.points_allowed}</td>
    <td className="py-3 px-4 text-center">
      <span className={standing.point_differential >= 0 ? 'text-green-600' : 'text-red-600'}>
        {standing.point_differential >= 0 ? '+' : ''}{standing.point_differential}
      </span>
    </td>
  </tr>
);

// Pool Game Card Component
const PoolGameCard = ({ game, canEdit, onUpdateScore }) => {
  const [editing, setEditing] = useState(false);
  const [score1, setScore1] = useState(game.team1_score || 0);
  const [score2, setScore2] = useState(game.team2_score || 0);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdateScore(game.id, score1, score2);
    setEditing(false);
    setSaving(false);
  };

  return (
    <Card className="mb-4" data-testid={`pool-game-${game.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className={`flex items-center justify-between p-2 rounded ${game.winner_id === game.team1_id ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
              <span className="font-medium">{game.team1_name}</span>
              {editing ? (
                <Input
                  type="number"
                  className="w-16 text-center"
                  value={score1}
                  onChange={(e) => setScore1(parseInt(e.target.value) || 0)}
                  min="0"
                />
              ) : (
                <span className="font-bold text-lg">{game.team1_score}</span>
              )}
            </div>
            <div className="text-center text-xs text-muted-foreground my-1">vs</div>
            <div className={`flex items-center justify-between p-2 rounded ${game.winner_id === game.team2_id ? 'bg-green-100 dark:bg-green-900/30' : ''}`}>
              <span className="font-medium">{game.team2_name}</span>
              {editing ? (
                <Input
                  type="number"
                  className="w-16 text-center"
                  value={score2}
                  onChange={(e) => setScore2(parseInt(e.target.value) || 0)}
                  min="0"
                />
              ) : (
                <span className="font-bold text-lg">{game.team2_score}</span>
              )}
            </div>
          </div>
          
          <div className="ml-4">
            <Badge variant={game.status === 'completed' ? 'default' : 'secondary'}>
              {game.status}
            </Badge>
          </div>
        </div>

        {canEdit && game.status !== 'completed' && (
          <div className="mt-4 flex gap-2">
            {editing ? (
              <>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Score'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Enter Score
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Pool Play Page
export const PoolPlay = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [pools, setPools] = useState([]);
  const [poolGames, setPoolGames] = useState([]);
  const [standings, setStandings] = useState({});
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  // Setup form state
  const [numPools, setNumPools] = useState(2);
  const [teamsPerPool, setTeamsPerPool] = useState(4);
  const [teamsAdvance, setTeamsAdvance] = useState(2);

  const { isConnected, subscribeToTournament, onGameUpdate } = useWebSocketContext();
  const canEdit = user?.role === 'admin' || user?.role === 'organizer' || user?.role === 'official';

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  // Subscribe to tournament updates
  useEffect(() => {
    if (tournamentId && isConnected) {
      subscribeToTournament(tournamentId);
    }
  }, [tournamentId, isConnected, subscribeToTournament]);

  // Listen for real-time updates
  useEffect(() => {
    const unsubscribe = onGameUpdate((message) => {
      if (message.update_type === 'pool_score_update') {
        const { game_id, team1_score, team2_score, winner_id } = message.data;
        
        setPoolGames(prev => 
          prev.map(game => 
            game.id === game_id 
              ? { ...game, team1_score, team2_score, winner_id, status: 'completed' }
              : game
          )
        );
        
        // Refresh standings
        fetchStandings();
        toast.info('Pool game score updated');
      }
    });
    
    return unsubscribe;
  }, [onGameUpdate]);

  const fetchData = async () => {
    try {
      const [tournamentRes, poolsRes, gamesRes] = await Promise.all([
        axios.get(`${API}/tournaments/${tournamentId}`),
        axios.get(`${API}/tournaments/${tournamentId}/pools`),
        axios.get(`${API}/tournaments/${tournamentId}/pool-games`)
      ]);
      
      setTournament(tournamentRes.data);
      setPools(poolsRes.data);
      setPoolGames(gamesRes.data);
      
      if (poolsRes.data.length > 0) {
        await fetchStandings();
      }
    } catch (error) {
      console.error('Error fetching pool play data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStandings = async () => {
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}/pool-standings`);
      setStandings(res.data);
    } catch (error) {
      console.error('Error fetching standings:', error);
    }
  };

  const handleSetupPools = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/tournaments/${tournamentId}/pools/setup`, {
        tournament_id: tournamentId,
        num_pools: numPools,
        teams_per_pool: teamsPerPool,
        teams_advance_per_pool: teamsAdvance
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Generate schedules for all pools
      await axios.post(`${API}/tournaments/${tournamentId}/pools/generate-all-schedules`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Pool play setup complete');
      setSetupMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup pools');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateScore = async (gameId, score1, score2) => {
    try {
      await axios.put(`${API}/pool-games/${gameId}/score`, {
        team1_score: score1,
        team2_score: score2
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Score updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update score');
    }
  };

  const handleAdvanceToBracket = async () => {
    setAdvancing(true);
    try {
      const res = await axios.post(`${API}/tournaments/${tournamentId}/advance-to-bracket`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.advancing_teams.length} teams advanced to bracket play!`);
      navigate(`/tournaments/${tournamentId}/bracket`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to advance teams');
    } finally {
      setAdvancing(false);
    }
  };

  // Check if all pool games are completed
  const allGamesCompleted = poolGames.length > 0 && poolGames.every(g => g.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">{tournament?.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-muted-foreground">Pool Play</p>
            {isConnected ? (
              <Badge variant="outline" className="text-green-500 border-green-500">
                <Wifi className="w-3 h-3 mr-1" />
                Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-500">
                <WifiOff className="w-3 h-3 mr-1" />
                Offline
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canEdit && pools.length === 0 && (
            <Button onClick={() => setSetupMode(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Setup Pool Play
            </Button>
          )}
          {canEdit && allGamesCompleted && !tournament?.pool_play_complete && (
            <Button onClick={handleAdvanceToBracket} disabled={advancing} className="bg-green-600 hover:bg-green-700">
              {advancing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ChevronRight className="mr-2 h-4 w-4" />
              )}
              Advance to Bracket
            </Button>
          )}
        </div>
      </div>

      {/* Setup Mode */}
      {setupMode && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Setup Pool Play</CardTitle>
            <CardDescription>Configure pools for round-robin play before bracket stage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Number of Pools</Label>
                <Input
                  type="number"
                  value={numPools}
                  onChange={(e) => setNumPools(parseInt(e.target.value) || 2)}
                  min="2"
                  max="8"
                  data-testid="num-pools-input"
                />
              </div>
              <div>
                <Label>Teams per Pool</Label>
                <Input
                  type="number"
                  value={teamsPerPool}
                  onChange={(e) => setTeamsPerPool(parseInt(e.target.value) || 4)}
                  min="3"
                  max="8"
                  data-testid="teams-per-pool-input"
                />
              </div>
              <div>
                <Label>Teams Advance per Pool</Label>
                <Input
                  type="number"
                  value={teamsAdvance}
                  onChange={(e) => setTeamsAdvance(parseInt(e.target.value) || 2)}
                  min="1"
                  max={teamsPerPool - 1}
                  data-testid="teams-advance-input"
                />
              </div>
            </div>
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm">
                <strong>Summary:</strong> {numPools} pools × {teamsPerPool} teams = {numPools * teamsPerPool} total teams needed
              </p>
              <p className="text-sm text-muted-foreground">
                Top {teamsAdvance} teams from each pool ({numPools * teamsAdvance} total) will advance to bracket play
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex gap-2">
            <Button onClick={handleSetupPools} disabled={generating} data-testid="setup-pools-btn">
              {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Create Pools & Schedule
            </Button>
            <Button variant="outline" onClick={() => setSetupMode(false)}>Cancel</Button>
          </CardFooter>
        </Card>
      )}

      {/* No pools yet */}
      {pools.length === 0 && !setupMode && (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">No Pool Play Setup</h2>
            <p className="text-muted-foreground mb-4">
              Set up pool play to have teams compete in round-robin groups before bracket play.
            </p>
            {canEdit && (
              <Button onClick={() => setSetupMode(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Setup Pool Play
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pools Tabs */}
      {pools.length > 0 && (
        <Tabs defaultValue={pools[0]?.name || 'standings'} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="standings">All Standings</TabsTrigger>
            {pools.map(pool => (
              <TabsTrigger key={pool.id} value={pool.name} data-testid={`tab-${pool.name}`}>
                {pool.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* All Standings Tab */}
          <TabsContent value="standings">
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(standings).map(([poolName, poolStandings]) => (
                <Card key={poolName}>
                  <CardHeader>
                    <CardTitle>{poolName} Standings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-4">#</th>
                            <th className="text-left py-2 px-4">Team</th>
                            <th className="text-center py-2 px-4">W</th>
                            <th className="text-center py-2 px-4">L</th>
                            <th className="text-center py-2 px-4">T</th>
                            <th className="text-center py-2 px-4">PF</th>
                            <th className="text-center py-2 px-4">PA</th>
                            <th className="text-center py-2 px-4">+/-</th>
                          </tr>
                        </thead>
                        <tbody>
                          {poolStandings.map((standing, index) => (
                            <StandingRow 
                              key={standing.team_id} 
                              standing={standing} 
                              rank={index + 1}
                              isAdvancing={index < (tournament?.teams_advance_per_pool || 2)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Individual Pool Tabs */}
          {pools.map(pool => (
            <TabsContent key={pool.id} value={pool.name}>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pool Games */}
                <div>
                  <h3 className="font-semibold mb-4">Games</h3>
                  {poolGames
                    .filter(g => g.pool_id === pool.id)
                    .map(game => (
                      <PoolGameCard
                        key={game.id}
                        game={game}
                        canEdit={canEdit}
                        onUpdateScore={handleUpdateScore}
                      />
                    ))}
                </div>

                {/* Pool Standings */}
                <div>
                  <h3 className="font-semibold mb-4">Standings</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-2">#</th>
                              <th className="text-left py-2 px-2">Team</th>
                              <th className="text-center py-2 px-2">W</th>
                              <th className="text-center py-2 px-2">L</th>
                              <th className="text-center py-2 px-2">PF</th>
                              <th className="text-center py-2 px-2">+/-</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(standings[pool.name] || []).map((standing, index) => (
                              <tr key={standing.team_id} className={`border-b ${index < (tournament?.teams_advance_per_pool || 2) ? 'bg-green-50 dark:bg-green-900/20' : ''}`}>
                                <td className="py-2 px-2">{index + 1}</td>
                                <td className="py-2 px-2 font-medium">{standing.team_name}</td>
                                <td className="text-center py-2 px-2">{standing.wins}</td>
                                <td className="text-center py-2 px-2">{standing.losses}</td>
                                <td className="text-center py-2 px-2 font-semibold">{standing.points_scored}</td>
                                <td className="text-center py-2 px-2">
                                  <span className={standing.point_differential >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {standing.point_differential >= 0 ? '+' : ''}{standing.point_differential}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Teams in Pool */}
                  <h3 className="font-semibold mt-6 mb-4">Teams</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="space-y-2">
                        {pool.team_names.map((name, i) => (
                          <div key={pool.team_ids[i]} className="flex items-center gap-2 p-2 bg-muted rounded">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>{name}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Pool Play Complete Banner */}
      {tournament?.pool_play_complete && (
        <Card className="mt-8 border-green-500 bg-green-50 dark:bg-green-900/20">
          <CardContent className="py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <h3 className="font-bold">Pool Play Complete!</h3>
                <p className="text-sm text-muted-foreground">Teams have advanced to bracket play</p>
              </div>
            </div>
            <Button onClick={() => navigate(`/tournaments/${tournamentId}/bracket`)}>
              <Trophy className="mr-2 h-4 w-4" />
              View Bracket
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
